import React, { useState, useCallback, useMemo } from 'react'
import { Box, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { PencilIcon, XIcon, CheckIcon, RotateCcwIcon } from 'lucide-react'
import { Button, Switch } from '@repo/ui'
import { Loader, ResetRoleModal } from 'components/feedback'

import {
  useRolesMatrix,
  useUpdateRolePermissions,
  useCreateRoleOverride,
  MatrixRoleItem,
  PermissionGroup,
  PermissionMeta,
} from 'hooks/api'

// ── Props ─────────────────────────────────────────────────────────────────────

interface PermissionsListProps {
  roleId: string
}

// ── Component ─────────────────────────────────────────────────────────────────

export const PermissionsList = ({ roleId }: PermissionsListProps) => {
  const theme = useTheme()
  const { data, isLoading, refetch } = useRolesMatrix()
  const updatePermissions = useUpdateRolePermissions()
  const createOverride = useCreateRoleOverride()

  const [editMode, setEditMode] = useState(false)
  const [localPerms, setLocalPerms] = useState<Set<string>>(new Set())
  const [resetModalOpen, setResetModalOpen] = useState(false)

  // ── Derived data ────────────────────────────────────────────────────────────

  const allRoles = data?.roles ?? []
  const groups = data?.groups ?? []

  const role: MatrixRoleItem | undefined = useMemo(
    () => allRoles.find((r: MatrixRoleItem) => r.id === roleId),
    [allRoles, roleId],
  )

  /**
   * Build a lookup: permissionName → permissionId
   * Sourced from ALL roles in the matrix (names are globally unique; first-seen wins).
   */
  const nameToId = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {}
    for (const r of allRoles) {
      r.permissionNames.forEach((name: string, idx: number) => {
        if (!map[name] && r.permissionIds[idx]) {
          map[name] = r.permissionIds[idx]
        }
      })
    }
    return map
  }, [allRoles])

  // ── Handlers ────────────────────────────────────────────────────────────────

  const enterEditMode = useCallback(() => {
    if (!role) return
    setLocalPerms(new Set(role.permissionNames))
    setEditMode(true)
  }, [role])

  const cancelEdit = useCallback(() => {
    setLocalPerms(new Set())
    setEditMode(false)
  }, [])

  /** Whether a permission is currently on (respects edit vs view mode). */
  const isOn = useCallback(
    (permName: string): boolean => {
      if (editMode) return localPerms.has(permName)
      return role?.permissionNames.includes(permName) ?? false
    },
    [editMode, localPerms, role],
  )

  const togglePerm = useCallback(
    (permName: string) => {
      if (!editMode) return
      setLocalPerms((prev) => {
        const next = new Set(prev)
        if (next.has(permName)) {
          next.delete(permName)
        } else {
          next.add(permName)
        }
        return next
      })
    },
    [editMode],
  )

  const toggleGroup = useCallback(
    (group: PermissionGroup) => {
      if (!editMode) return
      const allOn = group.permissions.every((p: PermissionMeta) => isOn(p.name))
      setLocalPerms((prev) => {
        const next = new Set(prev)
        if (allOn) {
          group.permissions.forEach((p: PermissionMeta) => next.delete(p.name))
        } else {
          group.permissions.forEach((p: PermissionMeta) => next.add(p.name))
        }
        return next
      })
    },
    [editMode, isOn],
  )

  /** True when the local edit set differs from the server state. */
  const isDirty = useMemo(() => {
    if (!editMode || !role) return false
    const serverSet = new Set(role.permissionNames)
    if (serverSet.size !== localPerms.size) return true
    for (const name of Array.from(localPerms)) {
      if (!serverSet.has(name)) return true
    }
    return false
  }, [editMode, localPerms, role])

  const isPending = updatePermissions.isPending || createOverride.isPending

  const handleSave = useCallback(async () => {
    if (!role) return

    const permissionIds = Array.from(localPerms)
      .map((name) => nameToId[name])
      .filter(Boolean)

    try {
      if (role.builtinRoleId && !role.isOverridden && !role.isCustom) {
        // Unmodified built-in: create/replace an override
        await createOverride.mutateAsync({ roleId: role.builtinRoleId, permissionIds })
      } else {
        // Override or standalone custom: update via the existing endpoint
        await updatePermissions.mutateAsync({ id: role.id, permissionIds })
      }
      setLocalPerms(new Set())
      setEditMode(false)
      refetch()
    } catch {
      // Error notification already fired by the respective mutation hooks
    }
  }, [role, localPerms, nameToId, createOverride, updatePermissions, refetch])

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) return <Loader />
  if (!data || !role) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header row: "Permissions" title + info icon + Edit/Save/Cancel */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 4, flexShrink: 0 }}
      >
        <Typography variant="h6" fontWeight={600} fontSize={20}>
          Permissions
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          {!editMode ? (
            <Button
              variant="secondary"
              onClick={enterEditMode}
              startIcon={<PencilIcon size={14} strokeWidth={1.5} />}
            >
              Edit
            </Button>
          ) : (
            <>
              {/* Reset lives in edit mode (parity with the permissions matrix):
                  revert a Modified built-in to the global default. */}
              {role.isOverridden && (
                <Tooltip
                  title="Revert this role to the built-in default permissions, discarding your company's changes."
                  arrow
                >
                  <span>
                    <Button
                      variant="secondary"
                      onClick={() => setResetModalOpen(true)}
                      startIcon={<RotateCcwIcon size={14} strokeWidth={1.5} />}
                    >
                      Reset
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="secondary"
                onClick={cancelEdit}
                startIcon={<XIcon size={14} strokeWidth={1.5} />}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!isDirty || isPending}
                onClick={handleSave}
                startIcon={<CheckIcon size={14} strokeWidth={1.5} />}
              >
                Save
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Permission groups + rows — scrolls within the card, scrollbar hidden. */}
      <Stack
        spacing={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 1,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {groups.map((group: PermissionGroup, gIdx: number) => {
          const allOn = group.permissions.every((p: PermissionMeta) => isOn(p.name))
          const someOn = group.permissions.some((p: PermissionMeta) => isOn(p.name))
          const indeterminate = someOn && !allOn

          return (
            <Box key={group.key}>
              {/* Group header row — shaded background */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 3,
                  py: 2,
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {group.label}
                </Typography>
                <Switch
                  checked={indeterminate ? true : allOn}
                  disabled={!editMode}
                  onChange={() => toggleGroup(group)}
                  size="small"
                  sx={{
                    opacity: indeterminate ? 0.5 : editMode ? 1 : 0.6,
                  }}
                />
              </Stack>

              {/* Individual permission rows */}
              {group.permissions.map((perm: PermissionMeta, pIdx: number) => {
                const isLastPermInLastGroup =
                  gIdx === groups.length - 1 && pIdx === group.permissions.length - 1
                return (
                  <Box key={perm.name}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ px: 3, py: 2.5 }}
                    >
                      <Typography variant="body2">{perm.label}</Typography>
                      <Switch
                        checked={isOn(perm.name)}
                        disabled={!editMode}
                        onChange={() => togglePerm(perm.name)}
                        size="small"
                        sx={{ opacity: editMode ? 1 : 0.6 }}
                      />
                    </Stack>
                    {!isLastPermInLastGroup && (
                      <Divider sx={{ mx: 3 }} />
                    )}
                  </Box>
                )
              })}
            </Box>
          )
        })}
      </Stack>

      <ResetRoleModal
        role={role}
        open={resetModalOpen}
        // Reset reverts to default — a decisive action: exit edit mode and
        // refetch so the list reflects fresh server state (parity with the
        // matrix's edit-mode reset).
        onReset={() => {
          cancelEdit()
          refetch()
        }}
        onClose={() => setResetModalOpen(false)}
      />
    </Box>
  )
}
